/* eslint-disable rxjs/no-unsafe-switchmap */
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap, tap, filter, distinctUntilChanged, take, delay } from 'rxjs/operators';

import { getCategory } from '../../../store/category-page.selectors';
import { GridDefault, GridStudio } from '../models/grid.models';
import { CategoryGridService } from '../services/category-grid.service';

import {
    ChangeCategory,
    SetGridTypeCategoryGrid,
    LoadFirstPageStudio,
    LoadNextPageStudioSuccess,
    LoadNextPageStudio,
    ScrollToPositionId,
    InitCategoryGrid,
    LoadNextPageGrid,
    LoadNextPageGridSuccess,
    LoadFirstPageGrid,
    LoadLastPageGridSuccess,
    LoadLastPageStudioSuccess,
    SetTotalQuantityProducts,
} from './category-grid.actions';
import {
    getGridDefault,
    getGridStudio,
    getGridCategory,
    getGridPageAllIds,
    getTotalQuantityProducts,
} from './category-grid.selectors';

import { Category } from '@core/domain/models/category.models';
import { SetProductsId } from '@pages/category/store/category-page.actions';
import { getGridType } from '@pages/category/store/grid-type/grid-type.selectors';
import { getPrevRouteProduct } from '@pages/product/store/product-page.selectors';
import { GridTypes } from '@shared/models/grid-type.model';
import { ProductNavigationData, getProductNavigationData, PreviousRouteNavigation } from '@shared/navigation';

@Injectable()
export class CategoryGridEffects {
    private readonly getProductNavigation$: Observable<ProductNavigationData> = this.store.select(getProductNavigationData);

    private readonly getPrevRouteProduct$: Observable<PreviousRouteNavigation> = this.store.select(getPrevRouteProduct);

    private readonly getGridType$: Observable<GridTypes> = this.store.select(getGridType);

    private readonly getGridDefault$: Observable<GridDefault> = this.store.select(getGridDefault);

    private readonly getGridStudio$: Observable<GridStudio> = this.store.select(getGridStudio);

    private readonly getCategory$: Observable<Category> = this.store.select(getCategory);

    private readonly getGridCategory$: Observable<string> = this.store.select(getGridCategory);

    private readonly getAllGridPageIds$: Observable<number[]> = this.store.select(getGridPageAllIds);

    private readonly totalQuantityProducts$: Observable<number> = this.store.select(getTotalQuantityProducts);

    constructor(
        private readonly actions$: Actions,
        private readonly categoryGridService: CategoryGridService,
        private readonly store: Store,
        private readonly viewportScroller: ViewportScroller,
        @Inject(DOCUMENT) private readonly document: Document,
    ) { }

    public initModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitCategoryGrid),
            switchMap(() => this.getCategory$),
            concatLatestFrom(() => this.getGridCategory$),
            filter(([category, lastCategoryId]) => !!category.id && category.id !== lastCategoryId),
            map(([category]) => ChangeCategory({ category })),
        );
    });

    public onChangeCategoryForceGrid$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCategory),
            filter((category) => !!category),
            map(() => SetGridTypeCategoryGrid({ initGridType: true }))
        );
    });

    public seeIfReturnToNavigateProduct$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitCategoryGrid),
            switchMap(() => this.getCategory$.pipe(
                filter((category) => !!category),
                take(1),
            )),
            concatLatestFrom(() => this.getGridCategory$),
            filter(([category, lastCategoryId]) => !!category.id && category.id === lastCategoryId),
            concatLatestFrom(() => [this.getPrevRouteProduct$, this.getProductNavigation$]),
            map(([[category], prevRouteNavigationData, productNavigationData]): string => {
                if (productNavigationData && category
                    && prevRouteNavigationData.page === 'ItxCategoryPage'
                    && category.id === productNavigationData.categoryId) {
                    return productNavigationData.productId;
                }
                return null;
            }),
            filter(productId => !!productId),
            map((productId) => {
                return ScrollToPositionId({ id: productId });
            }),
        );
    });

    // Efecto de inicialización de la Grid de ID's
    public loadFirstPageDefaultOnChangeCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCategory, SetProductsId),
            switchMap(() => this.categoryGridService.mapStateGridDefault()),
            map((gridDefault: GridDefault) => LoadFirstPageGrid({ grid: gridDefault })),
        );
    });

    // Efecto de inicialización de la Grid de GridElements (Templates de studio)
    public loadFirstPageStudioOnChangeCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCategory),
            switchMap(() => this.categoryGridService.mapStateGridStudio()),
            map((gridStudio: GridStudio) => LoadFirstPageStudio({ grid: gridStudio })),
        );
    });

    // Efecto de páginación para la vista por defecto de Id's
    public loadNextPageGridDefault$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNextPageGrid),
            concatLatestFrom(() => [this.getGridDefault$, this.getGridType$]),
            filter(([_, grid]) => {
                return grid.pageProductIds.length < grid.idsFiltered.length;
            }),
            map(([_, grid, gridType]) => {
                return CategoryGridService.sliceIdsWithPageSize(
                    gridType,
                    grid.idsFiltered,
                    grid.currentPage,
                    grid.pageProductIds.length
                );
            }),
            map(({ ids, page }) => LoadNextPageGridSuccess({ ids, page }))
        );
    });

    public loadLastPageGridDefault$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNextPageGrid),
            concatLatestFrom(() => this.getGridDefault$),
            filter(([_, grid]) => grid.pageProductIds.length >= grid.idsFiltered.length),
            distinctUntilChanged(([, b], [, d]) => b.currentPage === d.currentPage),
            map(() => LoadLastPageGridSuccess())
        );
    });

    // Efecto de páginación para la vista de GridElements (Templates de Studio)
    public loadNextPageGridStudio$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNextPageStudio),
            concatLatestFrom(() => [this.getGridStudio$, this.getGridType$]),
            filter(([_, grid]) => grid.currentPage < grid.totalPagesGridElements),
            map(([_, grid, gridType]) => {
                return CategoryGridService.sliceIdsWithPageSize(
                    gridType,
                    grid.gridElementsFiltered,
                    grid.currentPage
                );
            }),
            map(({ ids, page }) => LoadNextPageStudioSuccess({ ids, page }))
        );
    });

    public loadLastPageGridStudio$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNextPageStudio),
            concatLatestFrom(() => this.getGridStudio$),
            filter(([_, grid]) => grid.currentPage >= grid.totalPagesGridElements),
            distinctUntilChanged(([, b], [, d]) => b.currentPage === d.currentPage),
            map(() => LoadLastPageStudioSuccess())
        );
    });

    public whenGridTypeChangeLoadMoreProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetGridTypeCategoryGrid),
            map(() => {
                const gridType = this.store.selectSignal(getGridType)();
                if (gridType === GridTypes.ZARA_STUDIO) return LoadNextPageStudio();
                return LoadNextPageGrid();
            }),
        );
    });

    public scrollToPositionId$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ScrollToPositionId),
            concatLatestFrom(() => this.getProductNavigation$),
            // se añade este dalay para evitar que en IOS safari se quede la pagina en blanco y no haga el scroll
            delay(0),
            tap(([_, nav]) => {
                const pos = nav?.previousRoute?.scrollPosition;
                if (pos) {
                    const observer = new MutationObserver(() => {
                        let previousScrollHeight = this.document.body.scrollHeight;
                        const stabilityCheckInterval = setInterval(() => {
                            const currentScrollHeight = this.document.body.scrollHeight;

                            if (currentScrollHeight >= previousScrollHeight) {
                                this.viewportScroller.scrollToPosition([0, pos]);
                                clearInterval(stabilityCheckInterval);
                                observer.disconnect();
                            } else {
                                previousScrollHeight = currentScrollHeight;
                            }
                        }, 50);
                    });
                    observer.observe(this.document, { childList: true, subtree: true });
                }
            }),
        );
    }, { dispatch: false });

    public setTotalQuantityProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                LoadFirstPageGrid,
                LoadFirstPageStudio,
            ),
            concatLatestFrom(() => combineLatest([
                this.getAllGridPageIds$.pipe(map((allPageIds) => allPageIds.length)),
                this.totalQuantityProducts$,
            ])),
            map(([_, [allElementCount, totalQuantityProducts]]) => {
                return { allElementCount, totalQuantityProducts };
            }),
            filter(({ allElementCount, totalQuantityProducts }) => allElementCount !== totalQuantityProducts),
            map(({ allElementCount }) => SetTotalQuantityProducts({ totalQuantityProducts: allElementCount })),
        );
    });
}
